import {
  rrcProfileOptions,
  rrcProfileCodeLabel,
  rrcProfileDate,
} from '../rrc-profile-ui';
import {
  adjustFromTimeZone,
  adjustToTimeZone,
  rrcCompanyRoles,
  type RrcCompanyProfile,
  type Address,
} from '@ots-frontend/ots-data';

export type CompanyProfileDraft = {
  companyId: string;
  rrcDistrict: string;
  role: string;
  orgName: string;
  start?: number;
  end?: number;
  commercial: boolean;
  p5Number: string;
  permitNumber: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  address: Address;
  autoEp9SignerId: string;
  autoEp9SignerStrategy: string;
  autoEp10SignerId: string;
  autoEp10SignerStrategy: string;
};
export type CompanyProfileErrors = Partial<
  Record<keyof CompanyProfileDraft, string>
>;
export const companyProfileOptions = () => ({
  ...rrcProfileOptions(),
  roles: rrcCompanyRoles.map((code) => ({
    value: code.code,
    label: code.displayValue.defaultMessage ?? code.code,
  })),
});
export const companyProfileCodeLabel = (value?: string) =>
  companyProfileOptions().roles.find((option) => option.value === value)
    ?.label ?? rrcProfileCodeLabel(value);
export const companyProfileDate = rrcProfileDate;
const localTime = (value?: number) =>
  value == null ? undefined : adjustFromTimeZone(value).getTime();
const storedTime = (value?: number) =>
  value == null ? undefined : adjustToTimeZone(value).getTime();
export const toCompanyProfileDraft = (
  profile?: Partial<RrcCompanyProfile>
): CompanyProfileDraft => ({
  companyId: profile?.companyId ?? '',
  rrcDistrict: profile?.rrcDistrict ?? '',
  role: profile?.role ?? '',
  orgName: profile?.orgName ?? '',
  start: localTime(profile?.effectiveStartDttm),
  end: localTime(profile?.effectiveEndDttm),
  commercial: profile?.commercial ?? true,
  p5Number: profile?.p5Number ?? '',
  permitNumber: profile?.permitNumber ?? '',
  contactName: profile?.contactName ?? '',
  contactPhone: profile?.contactPhone ?? '',
  contactEmail: profile?.contactEmail ?? '',
  address: { ...profile?.address, country: profile?.address?.country ?? 'US' },
  autoEp9SignerId: profile?.autoEp9SignerId ?? '',
  autoEp9SignerStrategy: profile?.autoEp9SignerStrategy ?? '',
  autoEp10SignerId: profile?.autoEp10SignerId ?? '',
  autoEp10SignerStrategy: profile?.autoEp10SignerStrategy ?? '',
});
export const validateCompanyProfile = (
  draft: CompanyProfileDraft
): CompanyProfileErrors => {
  const errors: CompanyProfileErrors = {};
  for (const key of ['companyId', 'rrcDistrict', 'role', 'orgName'] as const)
    if (!draft[key].trim()) errors[key] = 'Required';
  if (draft.start == null || !Number.isFinite(draft.start))
    errors.start = 'An effective start date is required.';
  if (
    draft.end != null &&
    (!Number.isFinite(draft.end) ||
      (draft.start != null && draft.end <= draft.start))
  )
    errors.end = 'The end must be after the start.';
  if (
    draft.contactEmail &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contactEmail.trim())
  )
    errors.contactEmail = 'Enter a valid email address.';
  for (const form of ['autoEp9', 'autoEp10'] as const)
    if (
      draft[`${form}SignerStrategy`] === 'SELECTED' &&
      !draft[`${form}SignerId`]
    )
      errors[`${form}SignerId`] = 'Choose a signer for the Selected strategy.';
  return errors;
};
const optional = (value: string) => value.trim() || undefined;
/** Whitelist editable fields. Server-managed IDs, snapshots, and version metadata never come from a copied profile. */
export const toCompanyProfilePayload = (
  draft: CompanyProfileDraft,
  id?: string
): Partial<RrcCompanyProfile> => ({
  ...(id ? { id } : {}),
  companyId: draft.companyId,
  role: draft.role,
  rrcDistrict: draft.rrcDistrict,
  orgName: draft.orgName.trim(),
  effectiveStartDttm: storedTime(draft.start),
  effectiveEndDttm: storedTime(draft.end),
  commercial: draft.commercial,
  p5Number: optional(draft.p5Number),
  permitNumber: optional(draft.permitNumber),
  contactName: optional(draft.contactName),
  contactPhone: optional(draft.contactPhone),
  contactEmail: optional(draft.contactEmail),
  address: Object.fromEntries(
    Object.entries(draft.address).map(([key, value]) => [
      key,
      value?.trim() || undefined,
    ])
  ),
  autoEp9SignerId:
    draft.autoEp9SignerStrategy === 'SELECTED'
      ? optional(draft.autoEp9SignerId)
      : undefined,
  autoEp9SignerStrategy: optional(draft.autoEp9SignerStrategy),
  autoEp10SignerId:
    draft.autoEp10SignerStrategy === 'SELECTED'
      ? optional(draft.autoEp10SignerId)
      : undefined,
  autoEp10SignerStrategy: optional(draft.autoEp10SignerStrategy),
});
